<template>
  <div :class="fullView ? 'full-view' : 'variations-per-status-echarts'">
    <v-chart class="chart" :option="option" ref="chartRef" :init-options="{ renderer: 'svg' }"/>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { PieChart } from 'echarts/charts'
import { SVGRenderer } from 'echarts/renderers'
import { TooltipComponent, LegendComponent } from 'echarts/components'
import { use } from 'echarts/core'
import VChart from 'vue-echarts'
import { useChartResize } from '@/composables/useChartResize'
import { COLOR_PALETTE, getDefaultChartTextStyle, getPieNoDataOptions } from '@/helpers'
import { emitChartFinished } from '@/utils/insightsPdfUtils'

use([TooltipComponent, LegendComponent, PieChart, SVGRenderer])

export default {
  name: 'VariationsPerStatusChart',
  components: {
    VChart
  },
  props: {
    resultSet: {
      type: Object,
      required: true,
      default: () => ({})
    },
    fullView: {
      type: Boolean
    }
  },
  setup () {
    const { chartRef, handleResize } = useChartResize()

    return {
      chartRef,
      handleResize
    }
  },
  data () {
    return {
      option: {
        animation: !this.fullView,
        color: COLOR_PALETTE,
        title: {
          text: this.$t('insights.charts.variationsPerStatus.title'),
          left: 'center',
          top: 0,
          textStyle: getDefaultChartTextStyle(this.fullView)
        },
        tooltip: {
          trigger: 'item'
        },
        legend: {
          left: '5%',
          top: 'center',
          orient: 'vertical',
          type: 'scroll',
          textStyle: getDefaultChartTextStyle(this.fullView),
          width: '25%'
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['70%', this.fullView ? '50%' : '60%'],
            avoidLabelOverlap: false,
            label: {
              position: 'inner',
              ...getDefaultChartTextStyle(this.fullView),
              formatter: function (params) {
                return params.value
              }
            },
            data: []
          }
        ]
      }
    }
  },
  mounted () {
    this.updateData()
  },
  methods: {
    updateData () {
      const noDataOptions = getPieNoDataOptions(this.resultSet, this.fullView, this.option)
      if (noDataOptions) {
        this.option = {
          ...this.option,
          ...noDataOptions,
          title: this.option.title
        }
        emitChartFinished(this, true)
        return
      }

      const pivot = this.resultSet.chartPivot()
      const statusMap = this.statusesMap
      const labels = pivot.sort((r) => (r.x === 'contractor' ? -1 : 1))
        .map((r) => {
          if (statusMap && statusMap[r.x] && statusMap[r.x].name) {
            return statusMap[r.x].name.charAt(0).toUpperCase() + statusMap[r.x].name.slice(1)
          }
          return r.x.charAt(0).toUpperCase() + r.x.slice(1)
        })

      const data = pivot.map((r) => r['variations.count'])

      this.option.series[0].data = labels.map((label, index) => ({
        value: data[index],
        name: label
      }))
      emitChartFinished(this, false)
    }
  },
  computed: {
    ...mapState({
      statusesMap: (state) => state.statuses.reduce((acc, curr) => ({ ...acc, [curr.code]: curr }), {})
    })
  },
  watch: {
    resultSet: {
      handler () {
        this.updateData()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.variations-per-status-echarts {
  height: 250px;
}
.full-view {
  height: var(--full-view-chart-height);
  width: var(--full-view-chart-width);
}
</style>
